// components/flow/components/config/PhotoConfig.tsx
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Image as ImageIcon, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { useToast } from '@/hooks/use-toast.ts';
import { updateNode } from '@/services/api';
import type { FlowNode } from '@/types/flow.ts';
import MediaLibraryModal from '../MediaLibraryModal.tsx';

interface PhotoConfigProps {
    node: FlowNode;
    onClose: () => void;
    pageId: string;
}

export default function PhotoConfig({ node, onClose, pageId }: PhotoConfigProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [nodeName, setNodeName] = useState(node.data.node_name || '');
    const [mediaUrl, setMediaUrl] = useState(node.data.config.media_url || '');
    const [mediaFilename, setMediaFilename] = useState(node.data.config.media_filename || '');
    const [typingSeconds, setTypingSeconds] = useState(node.data.config?.delay_seconds || 0);

    const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);

    // Mutation para atualizar node
    const updateMutation = useMutation({
        mutationFn: (data: any) => updateNode(node.data.id, data),
        onSuccess: async () => {
            toast({
                title: 'Configurações salvas!',
                description: 'A foto foi atualizada com sucesso.',
            });
            await queryClient.invalidateQueries({ queryKey: ['funnel-details'] });
            onClose();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Handler de salvar
    const handleSave = () => {
        if (!mediaUrl) {
            toast({
                variant: 'destructive',
                title: 'Imagem obrigatória',
                description: 'Por favor, selecione uma imagem.',
            });
            return;
        }

        updateMutation.mutate({
            nome: nodeName,
            media_url: mediaUrl,
            media_filename: mediaFilename,
            media_type: 'image',
            delay_seconds: typingSeconds || null,
            page_id: pageId
        });
    };

    // Handler de seleção de mídia
    const handleMediaSelect = (media: any) => {
        setMediaUrl(media.file_url);
        setMediaFilename(media.filename);
    };

    return (
        <>
            <div className="flex-1 overflow-auto">
                <div className="p-6 space-y-6">
                    {/* Nome do Node */}
                    <div>
                        <Label htmlFor="node-name">Nome do Node (opcional)</Label>
                        <Input
                            id="node-name"
                            value={nodeName}
                            onChange={(e) => setNodeName(e.target.value)}
                            placeholder="Enviar Foto"
                            className="mt-1.5"
                            disabled={updateMutation.isPending}
                        />
                    </div>

                    {/* Imagem */}
                    <div>
                        <Label>Imagem *</Label>
                        {mediaUrl ? (
                            <div className="mt-1.5 space-y-2">
                                <div className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                                    <img
                                        src={mediaUrl}
                                        alt="Preview"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <span className="text-sm text-gray-700 truncate flex-1">
                                        {mediaFilename}
                                    </span>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setIsMediaLibraryOpen(true)}
                                        disabled={updateMutation.isPending}
                                    >
                                        Trocar
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => setIsMediaLibraryOpen(true)}
                                disabled={updateMutation.isPending}
                                className="mt-1.5 w-full aspect-square bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-colors flex flex-col items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ImageIcon className="w-12 h-12 text-gray-400" />
                                <span className="text-sm text-gray-600">Clique para selecionar imagem</span>
                            </button>
                        )}
                    </div>

                    {/* Digitando */}
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                            <MessageCircle className="w-3.5 h-3.5" />
                            Digitando... (segundos)
                        </Label>
                        <Input
                            type="number"
                            min={0}
                            max={30}
                            value={typingSeconds}
                            onChange={(e) => setTypingSeconds(Math.max(0, Math.min(30, parseInt(e.target.value) || 0)))}
                            placeholder="0"
                            className="h-10 w-32"
                            disabled={updateMutation.isPending}
                        />
                        <p className="text-xs text-gray-500">
                            Mostra "digitando..." por X segundos antes de enviar. 0 = desativado.
                        </p>
                    </div>

                    {/* Info Helper */}
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                        <p className="text-xs text-indigo-900">
                            💡 <strong>Dica:</strong> Esta foto será enviada diretamente para o usuário sem botões ou texto adicional.
                        </p>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
                <Button variant="outline" onClick={onClose} disabled={updateMutation.isPending}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                >
                    {updateMutation.isPending ? (
                        'Salvando...'
                    ) : (
                        <>
                            <Save className="w-4 h-4 mr-2" />
                            Salvar Configurações
                        </>
                    )}
                </Button>
            </div>

            {/* Media Library Modal */}
            <MediaLibraryModal
                isOpen={isMediaLibraryOpen}
                onClose={() => setIsMediaLibraryOpen(false)}
                onSelect={handleMediaSelect}
                pageId={pageId}
                mediaType="image"
            />
        </>
    );
}